import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import _ from 'lodash';

import Button from '../../common/Button';
import Loading from '../../common/Loading';
import { toastPromise } from '../../common/utils';
import { useGetPreDefinedExpenses } from '../../hooks/tanstack/useExpenseHook';
import {
  useGetModelOutput,
  useUpdateModel,
} from '../../hooks/tanstack/useModelsHook';
import { useGetScenarioListDisplay } from '../../hooks/tanstack/useScenariosHook';
import {
  useGetDistinguishExpenseSets,
  useGetDistinguishModel,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import CreateExpenseForm, {
  type SimpleExpenseSet,
} from '../expense/CreateExpenseForm';

const ExpenseConfiguration = () => {
  const form = useForm();

  const { handleSubmit, watch, setError } = form;

  const { projectId, orgId, modelId } = useGetRouteParams();

  const { model, isDefault, isRunningModelOutputServer } =
    useGetDistinguishModel();

  const { refetch: refetchScenarioListDisplay } = useGetScenarioListDisplay(
    {
      orgId,
      projectId,
      modelId,
    },
    {
      enabled: !!orgId && !!projectId && !!modelId,
    }
  );

  const { refetch: refetchModelOutput } = useGetModelOutput(
    {
      orgId,
      projectId,
      modelId,
    },
    {
      enabled: !!orgId && !!projectId && !!modelId,
    }
  );

  const modelDataset = model?.dataset;

  const allDefault = Object.values(watch('default') ?? {})
    .map((e: any) => Object.keys(e).filter((key) => e[key]))
    .flat();

  const allUser = Object.values(watch('user') ?? {})
    .map((e: any) => Object.keys(e).filter((key) => e[key]))
    .flat();

  const {
    data: predefinedExpenseSets,
    isLoading: isLoadingPredefinedExpenseSets,
  } = useGetPreDefinedExpenses(
    {
      orgId,
    },
    {
      enabled: !!orgId,
      refetchOnWindowFocus: false,
    }
  );

  const { userDefinedExpenseSets, isLoading: isLoadingUserDefinedExpenseSets } =
    useGetDistinguishExpenseSets({
      isRefetch: false,
    });

  const expenseSets = useMemo(
    () =>
      (
        predefinedExpenseSets?.map((i) => ({
          ...i,
          type: 'default',
          is_locked: false,
        })) as SimpleExpenseSet[]
      )
        ?.concat(
          (userDefinedExpenseSets?.map((i) => ({
            ...i,
            type: 'user',
            is_locked: i.is_locked,
          })) as SimpleExpenseSet[]) ?? []
        )
        ?.reduce((acc, cur) => {
          const groupOrder = cur.group.ifs_order;

          acc[groupOrder] = [...(acc[groupOrder] ?? []), cur];

          acc[groupOrder] = (acc[groupOrder] as SimpleExpenseSet[]).sort(
            (a, b) => a.group.ifs_order - b.group.ifs_order
          );

          return acc;
        }, [] as SimpleExpenseSet[][]),
    [predefinedExpenseSets, userDefinedExpenseSets]
  );

  const { mutateAsync: updateModel, isPending: isUpdatingModel } =
    useUpdateModel({
      onSuccess: () => {
        refetchScenarioListDisplay();
        refetchModelOutput();
      },
    });

  const handleOnSubmit = () => {
    if (!orgId || !projectId || !model || !modelDataset) {
      return;
    }

    const all = {} as Record<string, boolean[]>;

    Object.entries(watch('default')).forEach(([key, value]) => {
      const userExpense = watch('user')?.[key] ?? [];

      all[key] = (value as boolean[]).concat(userExpense);
    });

    const isError = Object.entries(all).some(([key, value]) => {
      if (value.filter((v) => v).length === 0) {
        setError('error', {
          message: `${key} is required at least one expense.`,
        });
        return true;
      }
    });

    if (isError) {
      return;
    }

    const res = updateModel({
      orgId,
      projectId,
      modelId,
      data: {
        name: model.name,
        predefined_expenses: allDefault.map(Number),
        userdefined_expenses: allUser.map(Number),
        dataset: modelDataset.id,
      },
    });

    toastPromise({
      promise: res,
      content: 'Expense set updated successfully',
    });
  };

  const isLocked = model?.is_locked ?? false;

  const isRunning = isRunningModelOutputServer;

  const isDirty = !(
    _.isEqual(model?.predefined_expenses, allDefault.map(Number)) &&
    _.isEqual(model?.userdefined_expenses, allUser.map(Number))
  );

  const isDisable = isLocked || isRunning;

  return (
    <div>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(handleOnSubmit)}>
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-bold">Expense</h1>
              {!model?.is_locked && isDefault && (
                <Button
                  variant={
                    !isDirty || isRunning || isLocked || isUpdatingModel
                      ? 'outlined'
                      : 'contained_blue'
                  }
                  type="submit"
                  loading={isUpdatingModel}
                  disabled={!isDirty || isRunning || isLocked}
                >
                  Save
                </Button>
              )}
            </div>
            {isLoadingPredefinedExpenseSets ||
            isLoadingUserDefinedExpenseSets ? (
              <Loading />
            ) : (
              expenseSets && (
                <CreateExpenseForm
                  data={expenseSets}
                  disabled={isDisable}
                  isSubmitting={isUpdatingModel}
                />
              )
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default ExpenseConfiguration;
